import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { noise2D } from "@remotion/noise";

// Fixed particle anchor positions (deterministic grid)
const PARTICLES = Array.from({ length: 20 }, (_, i) => ({
  seedX: i * 0.41,
  seedY: i * 0.73,
  baseX: (i * 137.5) % 100, // golden-angle distribution across width %
  baseY: (i * 97.3) % 100, // golden-angle distribution across height %
  size: 2 + (i % 3),
}));

export const CorporateBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        background:
          "linear-gradient(160deg, #f8f9fc 0%, #eef2fb 60%, #f4f6fd 100%)",
        overflow: "hidden",
      }}
    >
      {/* ── SVG base: grid + large accent arc ── */}
      <svg
        width={width}
        height={height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <defs>
          <pattern
            id="corpGrid"
            x="0"
            y="0"
            width="60"
            height="60"
            patternUnits="userSpaceOnUse"
          >
            <line
              x1="0"
              y1="60"
              x2="60"
              y2="60"
              stroke="rgba(37,99,235,0.055)"
              strokeWidth="1"
            />
            <line
              x1="60"
              y1="0"
              x2="60"
              y2="60"
              stroke="rgba(37,99,235,0.055)"
              strokeWidth="1"
            />
          </pattern>
          {/* Noise-driven gradient for the radial glow */}
          <radialGradient id="corpGlow" cx="80%" cy="10%" r="55%">
            <stop offset="0%" stopColor="rgba(37,99,235,0.07)" />
            <stop offset="100%" stopColor="rgba(37,99,235,0)" />
          </radialGradient>
        </defs>

        {/* Grid */}
        <rect width={width} height={height} fill="url(#corpGrid)" />

        {/* Top-right glow halo */}
        <rect width={width} height={height} fill="url(#corpGlow)" />

        {/* ── Noise-driven floating connection lines between particles ── */}
        {PARTICLES.slice(0, 10).map((p, i) => {
          const next = PARTICLES[(i + 1) % 10];
          const ax =
            (p.baseX / 100) * width +
            noise2D("lx" + i, p.seedX, frame * 0.003) * 40;
          const ay =
            (p.baseY / 100) * height +
            noise2D("ly" + i, p.seedY, frame * 0.003) * 40;
          const bx =
            (next.baseX / 100) * width +
            noise2D("lx" + (i + 1), next.seedX, frame * 0.003) * 40;
          const by =
            (next.baseY / 100) * height +
            noise2D("ly" + (i + 1), next.seedY, frame * 0.003) * 40;
          const dist = Math.hypot(bx - ax, by - ay);
          const lineOpacity = dist < 280 ? 0.06 - (dist / 280) * 0.06 : 0;

          return (
            <line
              key={i}
              x1={ax}
              y1={ay}
              x2={bx}
              y2={by}
              stroke={`rgba(37,99,235,${lineOpacity.toFixed(3)})`}
              strokeWidth="1"
            />
          );
        })}

        {/* ── Noise-driven floating data particles (circles) ── */}
        {PARTICLES.map((p, i) => {
          const px =
            (p.baseX / 100) * width +
            noise2D("px" + i, p.seedX, frame * 0.0025) * 50;
          const py =
            (p.baseY / 100) * height +
            noise2D("py" + i, p.seedY, frame * 0.002) * 60;
          const opacity =
            (noise2D("po" + i, p.seedX + 5, frame * 0.004) + 1) * 0.5 * 0.22 +
            0.04;
          const r =
            p.size +
            (noise2D("pr" + i, p.seedY + 3, frame * 0.003) + 1) * 0.5 * 1.5;
          const isFilled = i % 3 === 0;

          return (
            <circle
              key={i}
              cx={px}
              cy={py}
              r={r}
              fill={isFilled ? `rgba(37,99,235,${opacity.toFixed(3)})` : "none"}
              stroke={
                !isFilled
                  ? `rgba(37,99,235,${(opacity * 0.6).toFixed(3)})`
                  : "none"
              }
              strokeWidth={!isFilled ? 1 : 0}
            />
          );
        })}

        {/* ── Noise-driven diagonal accent bar ── */}
        {[0, 22, 44].map((offset, i) => {
          const drift = noise2D("bd" + i, i * 0.8, frame * 0.002) * 8;
          return (
            <rect
              key={i}
              x={-30 + offset + drift}
              y={height * 0.7}
              width={i === 0 ? 4 : i === 1 ? 3 : 2}
              height={height * 0.6}
              fill={`rgba(37,99,235,${[0.07, 0.045, 0.025][i]})`}
              transform={`rotate(-40, ${width * 0.04}, ${height * 0.7})`}
            />
          );
        })}
      </svg>

      {/* ── Subtle bottom-right corner warm glow (data center warmth) ── */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "40%",
          height: "30%",
          background:
            "radial-gradient(ellipse at 100% 100%, rgba(96,165,250,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* ── Vignette ── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(26,43,94,0.04) 100%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
